import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes,
  FaSpinner,
  FaBook,
  FaGraduationCap,
  FaUniversity,
  FaCheck,
  FaBan,
  FaUpload,
  FaDownload,
  FaFilter,
  FaSearch,
  FaFileImport,
  FaFileExport,
  FaInfoCircle,
  FaDatabase,
  FaCalendarAlt,
  FaBuilding,
  FaCode,
  FaUsers,
  FaEye,
  FaEyeSlash,
  FaShieldAlt,
  FaRocket,
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";
import { useSelector } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";

const AdminSGPA = () => {
  const { user } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState("schemes");
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(false);

  // ==================== SCHEMES STATE ====================
  const [schemes, setSchemes] = useState([]);
  const [showSchemeModal, setShowSchemeModal] = useState(false);
  const [editingScheme, setEditingScheme] = useState(null);
  const [schemeForm, setSchemeForm] = useState({
    scheme: "",
    name: "",
    description: "",
  });

  // ==================== BRANCHES STATE ====================
  const [branches, setBranches] = useState([]);
  const [showBranchModal, setShowBranchModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [branchForm, setBranchForm] = useState({
    code: "",
    name: "",
    fullName: "",
  });

  // ==================== SUBJECTS STATE ====================
  const [subjects, setSubjects] = useState([]);
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [subjectForm, setSubjectForm] = useState({
    subjectCode: "",
    subjectName: "",
    credits: 3,
    scheme: "",
    branch: "",
    semester: 1,
    cycle: "",
    isElective: false,
  });
  const [filters, setFilters] = useState({
    scheme: "",
    branch: "",
    semester: "",
  });

  // ==================== BULK IMPORT STATE ====================
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkData, setBulkData] = useState("");
  const [bulkFormat, setBulkFormat] = useState("json");
  const [bulkPreview, setBulkPreview] = useState([]);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [exportFormat, setExportFormat] = useState("json");

  const semesters = [1, 2, 3, 4, 5, 6, 7, 8];

  // Check dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    fetchData();
  }, [activeTab, filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      if (activeTab === "schemes") {
        const response = await axios.get(
          "/api/sgpa/admin/schemes",
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setSchemes(response.data);
      } else if (activeTab === "branches") {
        const response = await axios.get(
          "/api/sgpa/admin/branches",
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setBranches(response.data);
      } else if (activeTab === "subjects") {
        const params = {};
        if (filters.scheme) params.scheme = filters.scheme;
        if (filters.branch) params.branch = filters.branch;
        if (filters.semester) params.semester = filters.semester;

        const response = await axios.get(
          "/api/sgpa/admin/subjects",
          {
            params,
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setSubjects(response.data);
      }
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  // ==================== SCHEME CRUD ====================
  const handleSaveScheme = async () => {
    if (!schemeForm.scheme || !schemeForm.name) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (editingScheme) {
        await axios.put(
          `/api/sgpa/admin/schemes/${editingScheme._id}`,
          schemeForm,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        toast.success("Scheme updated successfully");
      } else {
        await axios.post(
          "/api/sgpa/admin/schemes",
          schemeForm,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        toast.success("New scheme created successfully!");
      }
      fetchData();
      setShowSchemeModal(false);
      setSchemeForm({ scheme: "", name: "", description: "" });
      setEditingScheme(null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save scheme");
    }
  };

  const handleDeleteScheme = async (scheme) => {
    if (
      !window.confirm(
        `⚠️ Delete "${scheme.name}"?\n\nThis will also delete ALL subjects under this scheme. This action cannot be undone!`,
      )
    )
      return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `/api/sgpa/admin/schemes/${scheme._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Scheme deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete scheme");
    }
  };

  const handleToggleSchemeStatus = async (scheme) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `/api/sgpa/admin/schemes/${scheme._id}`,
        {
          ...scheme,
          isActive: !scheme.isActive,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Scheme ${!scheme.isActive ? "activated" : "deactivated"}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // ==================== BRANCH CRUD ====================
  const handleSaveBranch = async () => {
    if (!branchForm.code || !branchForm.name) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (editingBranch) {
        await axios.put(
          `/api/sgpa/admin/branches/${editingBranch._id}`,
          branchForm,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        toast.success("Branch updated successfully");
      } else {
        await axios.post(
          "/api/sgpa/admin/branches",
          branchForm,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        toast.success("New branch added successfully!");
      }
      fetchData();
      setShowBranchModal(false);
      setBranchForm({ code: "", name: "", fullName: "" });
      setEditingBranch(null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save branch");
    }
  };

  const handleDeleteBranch = async (branch) => {
    if (
      !window.confirm(
        `⚠️ Delete "${branch.name}"?\n\nThis will also delete ALL subjects under this branch. This action cannot be undone!`,
      )
    )
      return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `/api/sgpa/admin/branches/${branch._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Branch deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete branch");
    }
  };

  const handleToggleBranchStatus = async (branch) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `/api/sgpa/admin/branches/${branch._id}`,
        {
          ...branch,
          isActive: !branch.isActive,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Branch ${!branch.isActive ? "activated" : "deactivated"}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // ==================== SUBJECT CRUD ====================
  const handleSaveSubject = async () => {
    if (
      !subjectForm.subjectCode ||
      !subjectForm.subjectName ||
      subjectForm.credits === undefined ||
      subjectForm.credits === null ||
      !subjectForm.scheme ||
      !subjectForm.branch
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    if (isNaN(subjectForm.credits) || subjectForm.credits < 0) {
      toast.error("Credits must be a valid number (0 or greater)");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (editingSubject) {
        await axios.put(
          `/api/sgpa/admin/subjects/${editingSubject._id}`,
          subjectForm,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        toast.success("Subject updated successfully");
      } else {
        await axios.post(
          "/api/sgpa/admin/subjects",
          subjectForm,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        toast.success("New subject added successfully!");
      }
      fetchData();
      setShowSubjectModal(false);
      resetSubjectForm();
      setEditingSubject(null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save subject");
    }
  };

  const handleDeleteSubject = async (subject) => {
    if (!window.confirm(`Delete "${subject.subjectName}"?`)) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `/api/sgpa/admin/subjects/${subject._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Subject deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete subject");
    }
  };

  const resetSubjectForm = () => {
    setSubjectForm({
      subjectCode: "",
      subjectName: "",
      credits: 3,
      scheme: "",
      branch: "",
      semester: 1,
      cycle: "",
      isElective: false,
    });
  };

  // ==================== BULK IMPORT ====================
  const handleBulkImport = async () => {
    if (!bulkData.trim()) {
      toast.error("Please enter data to import");
      return;
    }

    setBulkImporting(true);
    try {
      let subjectsToImport = [];

      if (bulkFormat === "json") {
        subjectsToImport = JSON.parse(bulkData);
      } else if (bulkFormat === "csv") {
        const lines = bulkData.trim().split("\n");
        const headers = lines[0].split(",").map((h) => h.trim());
        subjectsToImport = lines.slice(1).map((line) => {
          const values = line.split(",").map((v) => v.trim());
          const subject = {};
          headers.forEach((header, index) => {
            if (header === "credits" || header === "semester") {
              subject[header] = parseInt(values[index]);
            } else if (header === "isElective") {
              subject[header] = values[index] === "true";
            } else {
              subject[header] = values[index];
            }
          });
          return subject;
        });
      }

      const token = localStorage.getItem("token");
      const response = await axios.post(
        "/api/sgpa/admin/subjects/bulk",
        { subjects: subjectsToImport },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      toast.success(
        `✅ Imported ${response.data.success} subjects. Failed: ${response.data.failed}`,
      );
      fetchData();
      setShowBulkModal(false);
      setBulkData("");
      setBulkPreview([]);
    } catch (error) {
      toast.error("Failed to import data. Check format and try again.");
    } finally {
      setBulkImporting(false);
    }
  };

  const previewBulkData = () => {
    try {
      let subjectsToPreview = [];

      if (bulkFormat === "json") {
        subjectsToPreview = JSON.parse(bulkData);
      } else if (bulkFormat === "csv") {
        const lines = bulkData.trim().split("\n");
        const headers = lines[0].split(",").map((h) => h.trim());
        subjectsToPreview = lines.slice(1, 11).map((line) => {
          const values = line.split(",").map((v) => v.trim());
          const subject = {};
          headers.forEach((header, index) => {
            subject[header] = values[index];
          });
          return subject;
        });
      }

      setBulkPreview(
        subjectsToPreview.map((sub, idx) => ({
          ...sub,
          importOrder: idx + 1,
        })),
      );
      toast.success(
        `Preview shows ${subjectsToPreview.length} subjects in order`,
      );
    } catch (error) {
      toast.error("Invalid data format. Please check your input.");
      setBulkPreview([]);
    }
  };

  // ==================== EXPORT DATA ====================
  const handleExportData = () => {
    try {
      let exportData = [];

      if (activeTab === "schemes") {
        exportData = schemes;
      } else if (activeTab === "branches") {
        exportData = branches;
      } else if (activeTab === "subjects") {
        exportData = subjects;
      }

      let exportString = "";
      if (exportFormat === "json") {
        exportString = JSON.stringify(exportData, null, 2);
      } else if (exportFormat === "csv" && exportData.length > 0) {
        const headers = Object.keys(exportData[0]);
        const csvRows = [
          headers.join(","),
          ...exportData.map((row) =>
            headers
              .map((header) => {
                const value = row[header];
                if (typeof value === "object") return JSON.stringify(value);
                return value || "";
              })
              .join(","),
          ),
        ];
        exportString = csvRows.join("\n");
      }

      const blob = new Blob([exportString], {
        type: exportFormat === "json" ? "application/json" : "text/csv",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${activeTab}_export.${exportFormat}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export successful");
    } catch (error) {
      toast.error("Failed to export data");
    }
  };

  const getFilteredData = (data, searchField = "name") => {
    if (!searchTerm) return data;
    return data.filter(
      (item) =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.scheme?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.subjectCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.subjectName?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  return (
    <div
      style={{
        minHeight: "calc(100vh - 200px)",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        padding: "40px 20px",
        position: "relative",
      }}
    >
      {/* Animated Background Grid */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage: `radial-gradient(circle at 1px 1px, ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.08)"} 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Floating Gradient Orbs */}
      <motion.div
        animate={{
          x: [0, 100, 0],
          y: [0, 50, 0],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          top: "10%",
          left: "-5%",
          width: "300px",
          height: "300px",
          background:
            "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <motion.div
        animate={{
          x: [0, -100, 0],
          y: [0, 80, 0],
        }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          bottom: "10%",
          right: "-5%",
          width: "350px",
          height: "350px",
          background:
            "radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, type: "spring", stiffness: 300 }}
          style={{
            textAlign: "center",
            marginBottom: "40px",
          }}
        >
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
            style={{ display: "inline-block", marginBottom: "1rem" }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                width: "70px",
                height: "70px",
                borderRadius: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto",
                boxShadow: "0 20px 40px -12px rgba(139,92,246,0.4)",
              }}
            >
              <FaDatabase size={32} color="white" />
            </div>
          </motion.div>
          <h1
            style={{
              fontSize: "42px",
              fontWeight: "bold",
              marginBottom: "12px",
              background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            SGPA <span style={{ color: "#8b5cf6" }}>System Manager</span>
          </h1>
          <p
            style={{
              fontSize: "16px",
              color: getTextSecondary(),
              maxWidth: "700px",
              margin: "0 auto",
            }}
          >
            Manage schemes, branches, and subjects dynamically. All changes
            reflect immediately in the SGPA calculator.
          </p>
        </motion.div>

        {/* Quick Stats Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "20px",
            marginBottom: "32px",
          }}
        >
          {[
            {
              icon: <FaCalendarAlt />,
              label: "Total Schemes",
              value: schemes.length,
              gradient: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
            },
            {
              icon: <FaBuilding />,
              label: "Total Branches",
              value: branches.length,
              gradient: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
            },
            {
              icon: <FaBook />,
              label: "Total Subjects",
              value: subjects.length,
              gradient: "linear-gradient(135deg, #10b981, #059669)",
            },
          ].map((stat, idx) => (
            <motion.div
              key={idx}
              variants={itemVariants}
              whileHover={{ y: -5 }}
              style={{
                background: getCardBackground(),
                backdropFilter: "blur(20px)",
                borderRadius: "24px",
                padding: "20px",
                border: `1px solid ${getBorderColor()}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <p
                  style={{
                    color: getTextSecondary(),
                    fontSize: "14px",
                    marginBottom: "8px",
                  }}
                >
                  {stat.label}
                </p>
                <p
                  style={{
                    fontSize: "32px",
                    fontWeight: "bold",
                    background: stat.gradient,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {stat.value}
                </p>
              </div>
              <div
                style={{
                  width: "50px",
                  height: "50px",
                  background: "rgba(139,92,246,0.1)",
                  borderRadius: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "24px",
                  color: "#8b5cf6",
                }}
              >
                {stat.icon}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            gap: "8px",
            marginBottom: "24px",
            borderBottom: `2px solid ${getBorderColor()}`,
            flexWrap: "wrap",
            background: getCardBackground(),
            backdropFilter: "blur(20px)",
            padding: "8px 16px",
            borderRadius: "20px",
            border: `1px solid ${getBorderColor()}`,
          }}
        >
          {[
            {
              id: "schemes",
              label: "Schemes",
              icon: <FaCalendarAlt />,
              count: schemes.length,
            },
            {
              id: "branches",
              label: "Branches",
              icon: <FaBuilding />,
              count: branches.length,
            },
            {
              id: "subjects",
              label: "Subjects",
              icon: <FaBook />,
              count: subjects.length,
            },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: "12px 24px",
                background: "none",
                border: "none",
                borderBottom:
                  activeTab === tab.id ? "3px solid #8b5cf6" : "none",
                color: activeTab === tab.id ? "#8b5cf6" : getTextSecondary(),
                fontWeight: activeTab === tab.id ? "600" : "500",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "15px",
                transition: "all 0.3s",
              }}
            >
              {tab.icon}
              <span>{tab.label}</span>
              <span
                style={{
                  background:
                    activeTab === tab.id
                      ? "#8b5cf6"
                      : isDarkMode
                        ? "rgba(139,92,246,0.2)"
                        : "#e2e8f0",
                  color: activeTab === tab.id ? "white" : getTextSecondary(),
                  padding: "2px 8px",
                  borderRadius: "20px",
                  fontSize: "12px",
                  marginLeft: "4px",
                }}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Action Bar */}
        <div
          style={{
            background: getCardBackground(),
            backdropFilter: "blur(20px)",
            borderRadius: "20px",
            padding: "16px 20px",
            marginBottom: "24px",
            display: "flex",
            flexWrap: "wrap",
            gap: "16px",
            alignItems: "center",
            justifyContent: "space-between",
            border: `1px solid ${getBorderColor()}`,
          }}
        >
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            {activeTab === "schemes" && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setEditingScheme(null);
                  setSchemeForm({ scheme: "", name: "", description: "" });
                  setShowSchemeModal(true);
                }}
                style={{
                  padding: "10px 20px",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontWeight: "500",
                  transition: "all 0.3s",
                  boxShadow: "0 4px 14px 0 rgba(139,92,246,0.4)",
                }}
              >
                <FaPlus /> Add New Scheme
              </motion.button>
            )}
            {activeTab === "branches" && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setEditingBranch(null);
                  setBranchForm({ code: "", name: "", fullName: "" });
                  setShowBranchModal(true);
                }}
                style={{
                  padding: "10px 20px",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontWeight: "500",
                  transition: "all 0.3s",
                  boxShadow: "0 4px 14px 0 rgba(139,92,246,0.4)",
                }}
              >
                <FaPlus /> Add New Branch
              </motion.button>
            )}
            {activeTab === "subjects" && (
              <>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setEditingSubject(null);
                    resetSubjectForm();
                    setShowSubjectModal(true);
                  }}
                  style={{
                    padding: "10px 20px",
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontWeight: "500",
                    transition: "all 0.3s",
                    boxShadow: "0 4px 14px 0 rgba(139,92,246,0.4)",
                  }}
                >
                  <FaPlus /> Add Subject
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowBulkModal(true)}
                  style={{
                    padding: "10px 20px",
                    background: "linear-gradient(135deg, #10b981, #059669)",
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontWeight: "500",
                    transition: "all 0.3s",
                    boxShadow: "0 4px 14px 0 rgba(16,185,129,0.4)",
                  }}
                >
                  <FaFileImport /> Bulk Import
                </motion.button>
              </>
            )}
          </div>

          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <div style={{ position: "relative" }}>
              <FaSearch
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: getTextSecondary(),
                }}
              />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  padding: "10px 12px 10px 38px",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "12px",
                  fontSize: "14px",
                  width: "200px",
                  outline: "none",
                  transition: "all 0.3s",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
                onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                onBlur={(e) => (e.target.style.borderColor = getBorderColor())}
              />
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleExportData}
              style={{
                padding: "10px 16px",
                background: isDarkMode ? "rgba(139,92,246,0.1)" : "#f8f9fa",
                border: `2px solid ${getBorderColor()}`,
                borderRadius: "12px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "14px",
                color: getTextColor(),
                transition: "all 0.3s",
              }}
            >
              <FaFileExport /> Export
            </motion.button>
            <select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
              style={{
                padding: "10px 12px",
                border: `2px solid ${getBorderColor()}`,
                borderRadius: "12px",
                fontSize: "14px",
                outline: "none",
                cursor: "pointer",
                background: getInputBackground(),
                color: getTextColor(),
              }}
            >
              <option value="json">JSON</option>
              <option value="csv">CSV</option>
            </select>
          </div>
        </div>

        {/* Content Cards */}
        <AnimatePresence mode="wait">
          {activeTab === "schemes" && (
            <motion.div
              key="schemes"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              style={{
                background: getCardBackground(),
                backdropFilter: "blur(20px)",
                borderRadius: "28px",
                padding: "24px",
                border: `1px solid ${getBorderColor()}`,
              }}
            >
              {loading ? (
                <div style={{ textAlign: "center", padding: "60px" }}>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  >
                    <FaSpinner size={40} color="#8b5cf6" />
                  </motion.div>
                  <p style={{ marginTop: "16px", color: getTextSecondary() }}>
                    Loading schemes...
                  </p>
                </div>
              ) : getFilteredData(schemes).length === 0 ? (
                <div style={{ textAlign: "center", padding: "60px" }}>
                  <FaCalendarAlt size={48} color={getTextSecondary()} />
                  <p
                    style={{
                      color: getTextSecondary(),
                      marginTop: "16px",
                      marginBottom: "8px",
                    }}
                  >
                    No schemes found.
                  </p>
                  <p style={{ color: getTextSecondary(), fontSize: "14px" }}>
                    Click "Add New Scheme" to create one.
                  </p>
                </div>
              ) : (
                <div style={{ display: "grid", gap: "16px" }}>
                  {getFilteredData(schemes).map((scheme) => (
                    <motion.div
                      key={scheme._id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      whileHover={{ y: -2 }}
                      style={{
                        border: `1px solid ${getBorderColor()}`,
                        borderRadius: "20px",
                        padding: "20px",
                        transition: "all 0.3s",
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "16px",
                        background: getCardBackground(),
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            flexWrap: "wrap",
                            marginBottom: "8px",
                          }}
                        >
                          <h3
                            style={{
                              fontSize: "18px",
                              fontWeight: "bold",
                              color: getTextColor(),
                              margin: 0,
                            }}
                          >
                            {scheme.name}
                          </h3>
                          <span
                            style={{
                              padding: "4px 12px",
                              borderRadius: "20px",
                              fontSize: "12px",
                              fontWeight: "500",
                              background: scheme.isActive
                                ? "linear-gradient(135deg, #10b981, #059669)"
                                : "linear-gradient(135deg, #6c757d, #5a6268)",
                              color: "white",
                            }}
                          >
                            {scheme.isActive ? "Active" : "Inactive"}
                          </span>
                        </div>
                        <p
                          style={{
                            color: getTextSecondary(),
                            fontSize: "14px",
                            marginBottom: "4px",
                          }}
                        >
                          <strong>Scheme:</strong> {scheme.scheme}
                        </p>
                        {scheme.description && (
                          <p
                            style={{
                              color: getTextSecondary(),
                              fontSize: "13px",
                              marginTop: "8px",
                            }}
                          >
                            {scheme.description}
                          </p>
                        )}
                        <p
                          style={{
                            color: getTextSecondary(),
                            fontSize: "11px",
                            marginTop: "8px",
                          }}
                        >
                          Created:{" "}
                          {new Date(scheme.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleToggleSchemeStatus(scheme)}
                          style={{
                            padding: "8px 12px",
                            background: scheme.isActive
                              ? "linear-gradient(135deg, #f59e0b, #d97706)"
                              : "linear-gradient(135deg, #10b981, #059669)",
                            color: "white",
                            border: "none",
                            borderRadius: "10px",
                            cursor: "pointer",
                            fontSize: "13px",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          {scheme.isActive ? <FaBan /> : <FaCheck />}
                          {scheme.isActive ? "Deactivate" : "Activate"}
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            setEditingScheme(scheme);
                            setSchemeForm({
                              scheme: scheme.scheme,
                              name: scheme.name,
                              description: scheme.description || "",
                            });
                            setShowSchemeModal(true);
                          }}
                          style={{
                            padding: "8px 12px",
                            background:
                              "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                            color: "white",
                            border: "none",
                            borderRadius: "10px",
                            cursor: "pointer",
                            fontSize: "13px",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <FaEdit /> Edit
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleDeleteScheme(scheme)}
                          style={{
                            padding: "8px 12px",
                            background:
                              "linear-gradient(135deg, #ef4444, #dc2626)",
                            color: "white",
                            border: "none",
                            borderRadius: "10px",
                            cursor: "pointer",
                            fontSize: "13px",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <FaTrash /> Delete
                        </motion.button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {activeTab === "branches" && (
            <motion.div
              key="branches"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              style={{
                background: getCardBackground(),
                backdropFilter: "blur(20px)",
                borderRadius: "28px",
                padding: "24px",
                border: `1px solid ${getBorderColor()}`,
              }}
            >
              {loading ? (
                <div style={{ textAlign: "center", padding: "60px" }}>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  >
                    <FaSpinner size={40} color="#8b5cf6" />
                  </motion.div>
                  <p style={{ marginTop: "16px", color: getTextSecondary() }}>
                    Loading branches...
                  </p>
                </div>
              ) : getFilteredData(branches).length === 0 ? (
                <div style={{ textAlign: "center", padding: "60px" }}>
                  <FaBuilding size={48} color={getTextSecondary()} />
                  <p
                    style={{
                      color: getTextSecondary(),
                      marginTop: "16px",
                      marginBottom: "8px",
                    }}
                  >
                    No branches found.
                  </p>
                  <p style={{ color: getTextSecondary(), fontSize: "14px" }}>
                    Click "Add New Branch" to create one.
                  </p>
                </div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(350px, 1fr))",
                    gap: "16px",
                  }}
                >
                  {getFilteredData(branches).map((branch) => (
                    <motion.div
                      key={branch._id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      whileHover={{ y: -3 }}
                      style={{
                        border: `1px solid ${getBorderColor()}`,
                        borderRadius: "20px",
                        padding: "20px",
                        transition: "all 0.3s",
                        background: getCardBackground(),
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: "12px",
                        }}
                      >
                        <div>
                          <h3
                            style={{
                              fontSize: "18px",
                              fontWeight: "bold",
                              color: getTextColor(),
                              marginBottom: "4px",
                            }}
                          >
                            {branch.name}
                          </h3>
                          <p
                            style={{
                              color: getTextSecondary(),
                              fontSize: "13px",
                            }}
                          >
                            <strong>Code:</strong> {branch.code}
                          </p>
                        </div>
                        <span
                          style={{
                            padding: "4px 10px",
                            borderRadius: "20px",
                            fontSize: "11px",
                            fontWeight: "500",
                            background: branch.isActive
                              ? "linear-gradient(135deg, #10b981, #059669)"
                              : "linear-gradient(135deg, #6c757d, #5a6268)",
                            color: "white",
                          }}
                        >
                          {branch.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                      {branch.fullName && (
                        <p
                          style={{
                            color: getTextSecondary(),
                            fontSize: "12px",
                            marginBottom: "16px",
                          }}
                        >
                          {branch.fullName}
                        </p>
                      )}
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          marginTop: "12px",
                        }}
                      >
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleToggleBranchStatus(branch)}
                          style={{
                            flex: 1,
                            padding: "8px",
                            background: branch.isActive
                              ? "linear-gradient(135deg, #f59e0b, #d97706)"
                              : "linear-gradient(135deg, #10b981, #059669)",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "6px",
                          }}
                        >
                          {branch.isActive ? <FaBan /> : <FaCheck />}
                          {branch.isActive ? "Deactivate" : "Activate"}
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            setEditingBranch(branch);
                            setBranchForm({
                              code: branch.code,
                              name: branch.name,
                              fullName: branch.fullName || "",
                            });
                            setShowBranchModal(true);
                          }}
                          style={{
                            flex: 1,
                            padding: "8px",
                            background:
                              "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "6px",
                          }}
                        >
                          <FaEdit /> Edit
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleDeleteBranch(branch)}
                          style={{
                            flex: 1,
                            padding: "8px",
                            background:
                              "linear-gradient(135deg, #ef4444, #dc2626)",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "6px",
                          }}
                        >
                          <FaTrash /> Delete
                        </motion.button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {activeTab === "subjects" && (
            <motion.div
              key="subjects"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              style={{
                background: getCardBackground(),
                backdropFilter: "blur(20px)",
                borderRadius: "28px",
                padding: "24px",
                border: `1px solid ${getBorderColor()}`,
              }}
            >
              {/* Filters */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "16px",
                  marginBottom: "24px",
                  padding: "16px",
                  background: isDarkMode
                    ? "rgba(139,92,246,0.05)"
                    : "rgba(139,92,246,0.03)",
                  borderRadius: "16px",
                  border: `1px solid ${getBorderColor()}`,
                }}
              >
                <select
                  value={filters.scheme}
                  onChange={(e) =>
                    setFilters({ ...filters, scheme: e.target.value })
                  }
                  style={{
                    padding: "10px 12px",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    fontSize: "14px",
                    outline: "none",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                >
                  <option value="">All Schemes</option>
                  {schemes.map((s) => (
                    <option key={s._id} value={s.scheme}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <select
                  value={filters.branch}
                  onChange={(e) =>
                    setFilters({ ...filters, branch: e.target.value })
                  }
                  style={{
                    padding: "10px 12px",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    fontSize: "14px",
                    outline: "none",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                >
                  <option value="">All Branches</option>
                  {branches.map((b) => (
                    <option key={b._id} value={b.code}>
                      {b.name}
                    </option>
                  ))}
                </select>
                <select
                  value={filters.semester}
                  onChange={(e) =>
                    setFilters({ ...filters, semester: e.target.value })
                  }
                  style={{
                    padding: "10px 12px",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    fontSize: "14px",
                    outline: "none",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                >
                  <option value="">All Semesters</option>
                  {semesters.map((s) => (
                    <option key={s} value={s}>
                      Semester {s}
                    </option>
                  ))}
                </select>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() =>
                    setFilters({ scheme: "", branch: "", semester: "" })
                  }
                  style={{
                    padding: "10px 16px",
                    background: isDarkMode ? "rgba(139,92,246,0.1)" : "#e9ecef",
                    border: "none",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: getTextColor(),
                  }}
                >
                  Clear Filters
                </motion.button>
              </div>

              {loading ? (
                <div style={{ textAlign: "center", padding: "60px" }}>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  >
                    <FaSpinner size={40} color="#8b5cf6" />
                  </motion.div>
                  <p style={{ marginTop: "16px", color: getTextSecondary() }}>
                    Loading subjects...
                  </p>
                </div>
              ) : getFilteredData(subjects, "subjectName").length === 0 ? (
                <div style={{ textAlign: "center", padding: "60px" }}>
                  <FaBook size={48} color={getTextSecondary()} />
                  <p
                    style={{
                      color: getTextSecondary(),
                      marginTop: "16px",
                      marginBottom: "8px",
                    }}
                  >
                    No subjects found.
                  </p>
                  <p style={{ color: getTextSecondary(), fontSize: "14px" }}>
                    Click "Add Subject" to create one.
                  </p>
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "13px",
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          background: isDarkMode
                            ? "rgba(139,92,246,0.1)"
                            : "rgba(139,92,246,0.05)",
                          borderBottom: `2px solid ${getBorderColor()}`,
                        }}
                      >
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            width: "60px",
                            color: getTextColor(),
                          }}
                        >
                          #
                        </th>
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "left",
                            color: getTextColor(),
                          }}
                        >
                          Code
                        </th>
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "left",
                            color: getTextColor(),
                          }}
                        >
                          Subject Name
                        </th>
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            color: getTextColor(),
                          }}
                        >
                          Credits
                        </th>
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            color: getTextColor(),
                          }}
                        >
                          Scheme
                        </th>
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            color: getTextColor(),
                          }}
                        >
                          Branch
                        </th>
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            color: getTextColor(),
                          }}
                        >
                          Sem
                        </th>
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            color: getTextColor(),
                          }}
                        >
                          Cycle
                        </th>
                        <th
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            color: getTextColor(),
                          }}
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const sortedSubjects = [
                          ...getFilteredData(subjects, "subjectName"),
                        ].sort((a, b) => b.credits - a.credits);
                        return sortedSubjects.map((subject, index) => (
                          <tr
                            key={subject._id}
                            style={{
                              borderBottom: `1px solid ${getBorderColor()}`,
                            }}
                          >
                            <td
                              style={{
                                padding: "12px",
                                textAlign: "center",
                                color: getTextColor(),
                              }}
                            >
                              {index + 1}
                            </td>
                            <td
                              style={{
                                padding: "12px",
                                fontFamily: "monospace",
                                color: "#8b5cf6",
                              }}
                            >
                              {subject.subjectCode}
                            </td>
                            <td
                              style={{ padding: "12px", color: getTextColor() }}
                            >
                              {subject.subjectName}
                            </td>
                            <td
                              style={{ padding: "12px", textAlign: "center" }}
                            >
                              <span
                                style={{
                                  background:
                                    subject.credits >= 4
                                      ? "linear-gradient(135deg, #10b981, #059669)"
                                      : subject.credits >= 3
                                        ? "linear-gradient(135deg, #f59e0b, #d97706)"
                                        : "linear-gradient(135deg, #6c757d, #5a6268)",
                                  color: "white",
                                  padding: "2px 8px",
                                  borderRadius: "20px",
                                  fontSize: "11px",
                                  fontWeight: "bold",
                                  display: "inline-block",
                                }}
                              >
                                {subject.credits} credits
                              </span>
                            </td>
                            <td
                              style={{
                                padding: "12px",
                                textAlign: "center",
                                color: getTextColor(),
                              }}
                            >
                              {subject.scheme}
                            </td>
                            <td
                              style={{
                                padding: "12px",
                                textAlign: "center",
                                color: getTextColor(),
                              }}
                            >
                              {subject.branch}
                            </td>
                            <td
                              style={{
                                padding: "12px",
                                textAlign: "center",
                                color: getTextColor(),
                              }}
                            >
                              {subject.semester}
                            </td>
                            <td
                              style={{
                                padding: "12px",
                                textAlign: "center",
                                color: getTextColor(),
                              }}
                            >
                              {subject.cycle || "-"}
                            </td>
                            <td
                              style={{ padding: "12px", textAlign: "center" }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  gap: "6px",
                                  justifyContent: "center",
                                }}
                              >
                                <motion.button
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  onClick={() => {
                                    setEditingSubject(subject);
                                    setSubjectForm({
                                      subjectCode: subject.subjectCode,
                                      subjectName: subject.subjectName,
                                      credits: subject.credits,
                                      scheme: subject.scheme,
                                      branch: subject.branch,
                                      semester: subject.semester,
                                      cycle: subject.cycle || "",
                                      isElective: subject.isElective,
                                    });
                                    setShowSubjectModal(true);
                                  }}
                                  style={{
                                    padding: "6px 10px",
                                    background:
                                      "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <FaEdit /> Edit
                                </motion.button>
                                <motion.button
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  onClick={() => handleDeleteSubject(subject)}
                                  style={{
                                    padding: "6px 10px",
                                    background:
                                      "linear-gradient(135deg, #ef4444, #dc2626)",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <FaTrash /> Del
                                </motion.button>
                              </div>
                            </td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tips Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{
            marginTop: "32px",
            padding: "20px",
            background: isDarkMode ? "rgba(139,92,246,0.1)" : "#fff5e6",
            borderRadius: "20px",
            border: `1px solid ${getBorderColor()}`,
          }}
        >
          <h4
            style={{
              fontSize: "16px",
              fontWeight: "bold",
              color: "#8b5cf6",
              marginBottom: "12px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FaInfoCircle /> Admin Tips
          </h4>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "12px",
              fontSize: "13px",
              color: getTextSecondary(),
            }}
          >
            <div>
              • <strong>Schemes:</strong> Add new academic years (2023, 2024,
              etc.)
            </div>
            <div>
              • <strong>Branches:</strong> Add new specializations or
              departments
            </div>
            <div>
              • <strong>Subjects:</strong> Add for any scheme, branch, semester
              combination
            </div>
            <div>
              • <strong>Bulk Import:</strong> Use JSON/CSV to add multiple
              subjects at once
            </div>
            <div>
              • <strong>Export:</strong> Download data for backup or migration
            </div>
            <div>
              • <strong>Status:</strong> Deactivate old schemes/branches instead
              of deleting
            </div>
          </div>
        </motion.div>
      </div>

      {/* ==================== SCHEME MODAL ==================== */}
      {showSchemeModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
          onClick={() => setShowSchemeModal(false)}
        >
          <div
            style={{
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              borderRadius: "28px",
              maxWidth: "500px",
              width: "100%",
              padding: "30px",
              maxHeight: "90vh",
              overflow: "auto",
              border: `1px solid ${getBorderColor()}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
              }}
            >
              <h2
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {editingScheme ? "Edit Scheme" : "Add New Scheme"}
              </h2>
              <button
                onClick={() => setShowSchemeModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "20px",
                  cursor: "pointer",
                  color: getTextSecondary(),
                  padding: "8px",
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: getTextSecondary(),
                  fontWeight: "500",
                }}
              >
                Scheme Year *
              </label>
              <input
                type="text"
                value={schemeForm.scheme}
                onChange={(e) =>
                  setSchemeForm({ ...schemeForm, scheme: e.target.value })
                }
                placeholder="e.g., 2022, 2023, 2025"
                disabled={!!editingScheme}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "12px",
                  fontSize: "14px",
                  outline: "none",
                  background: editingScheme
                    ? isDarkMode
                      ? "rgba(0,0,0,0.3)"
                      : "#f8f9fa"
                    : getInputBackground(),
                  color: getTextColor(),
                }}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: getTextSecondary(),
                  fontWeight: "500",
                }}
              >
                Scheme Name *
              </label>
              <input
                type="text"
                value={schemeForm.name}
                onChange={(e) =>
                  setSchemeForm({ ...schemeForm, name: e.target.value })
                }
                placeholder="e.g., CBCS 2022 Scheme"
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "12px",
                  fontSize: "14px",
                  outline: "none",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
              />
            </div>

            <div style={{ marginBottom: "24px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: getTextSecondary(),
                  fontWeight: "500",
                }}
              >
                Description (Optional)
              </label>
              <textarea
                value={schemeForm.description}
                onChange={(e) =>
                  setSchemeForm({ ...schemeForm, description: e.target.value })
                }
                placeholder="Add description for this scheme..."
                rows="3"
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "12px",
                  fontSize: "14px",
                  outline: "none",
                  resize: "vertical",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
              />
            </div>

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={() => setShowSchemeModal(false)}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: isDarkMode ? "rgba(139,92,246,0.1)" : "#f8f9fa",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: getTextColor(),
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveScheme}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  boxShadow: "0 4px 14px 0 rgba(139,92,246,0.4)",
                }}
              >
                {editingScheme ? "Update Scheme" : "Create Scheme"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== BRANCH MODAL ==================== */}
      {showBranchModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
          onClick={() => setShowBranchModal(false)}
        >
          <div
            style={{
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              borderRadius: "28px",
              maxWidth: "500px",
              width: "100%",
              padding: "30px",
              maxHeight: "90vh",
              overflow: "auto",
              border: `1px solid ${getBorderColor()}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
              }}
            >
              <h2
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {editingBranch ? "Edit Branch" : "Add New Branch"}
              </h2>
              <button
                onClick={() => setShowBranchModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "20px",
                  cursor: "pointer",
                  color: getTextSecondary(),
                  padding: "8px",
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: getTextSecondary(),
                  fontWeight: "500",
                }}
              >
                Branch Code *
              </label>
              <input
                type="text"
                value={branchForm.code}
                onChange={(e) =>
                  setBranchForm({
                    ...branchForm,
                    code: e.target.value.toUpperCase(),
                  })
                }
                placeholder="e.g., CS, EC, ME"
                disabled={!!editingBranch}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "12px",
                  fontSize: "14px",
                  outline: "none",
                  background: editingBranch
                    ? isDarkMode
                      ? "rgba(0,0,0,0.3)"
                      : "#f8f9fa"
                    : getInputBackground(),
                  color: getTextColor(),
                }}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: getTextSecondary(),
                  fontWeight: "500",
                }}
              >
                Branch Name *
              </label>
              <input
                type="text"
                value={branchForm.name}
                onChange={(e) =>
                  setBranchForm({ ...branchForm, name: e.target.value })
                }
                placeholder="e.g., Computer Science Engineering"
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "12px",
                  fontSize: "14px",
                  outline: "none",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
              />
            </div>

            <div style={{ marginBottom: "24px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: getTextSecondary(),
                  fontWeight: "500",
                }}
              >
                Full Name (Optional)
              </label>
              <input
                type="text"
                value={branchForm.fullName}
                onChange={(e) =>
                  setBranchForm({ ...branchForm, fullName: e.target.value })
                }
                placeholder="Full branch name"
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "12px",
                  fontSize: "14px",
                  outline: "none",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
              />
            </div>

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={() => setShowBranchModal(false)}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: isDarkMode ? "rgba(139,92,246,0.1)" : "#f8f9fa",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: getTextColor(),
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveBranch}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  boxShadow: "0 4px 14px 0 rgba(139,92,246,0.4)",
                }}
              >
                {editingBranch ? "Update Branch" : "Create Branch"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== SUBJECT MODAL ==================== */}
      {showSubjectModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
          onClick={() => setShowSubjectModal(false)}
        >
          <div
            style={{
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              borderRadius: "28px",
              maxWidth: "700px",
              width: "100%",
              padding: "30px",
              maxHeight: "90vh",
              overflow: "auto",
              border: `1px solid ${getBorderColor()}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
              }}
            >
              <h2
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {editingSubject ? "Edit Subject" : "Add New Subject"}
              </h2>
              <button
                onClick={() => setShowSubjectModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "20px",
                  cursor: "pointer",
                  color: getTextSecondary(),
                  padding: "8px",
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: getTextSecondary(),
                    fontWeight: "500",
                  }}
                >
                  Subject Code *
                </label>
                <input
                  type="text"
                  value={subjectForm.subjectCode}
                  onChange={(e) =>
                    setSubjectForm({
                      ...subjectForm,
                      subjectCode: e.target.value,
                    })
                  }
                  placeholder="e.g., BCS301"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    fontSize: "14px",
                    outline: "none",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: getTextSecondary(),
                    fontWeight: "500",
                  }}
                >
                  Subject Name *
                </label>
                <input
                  type="text"
                  value={subjectForm.subjectName}
                  onChange={(e) =>
                    setSubjectForm({
                      ...subjectForm,
                      subjectName: e.target.value,
                    })
                  }
                  placeholder="e.g., Data Structures"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    fontSize: "14px",
                    outline: "none",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: getTextSecondary(),
                    fontWeight: "500",
                  }}
                >
                  Credits *
                </label>
                <input
                  type="number"
                  min="0"
                  max="10"
                  value={subjectForm.credits}
                  onChange={(e) =>
                    setSubjectForm({
                      ...subjectForm,
                      credits: parseInt(e.target.value),
                    })
                  }
                  placeholder="e.g., 3, 4"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    fontSize: "14px",
                    outline: "none",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: getTextSecondary(),
                    fontWeight: "500",
                  }}
                >
                  Scheme *
                </label>
                <select
                  value={subjectForm.scheme}
                  onChange={(e) =>
                    setSubjectForm({ ...subjectForm, scheme: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    fontSize: "14px",
                    outline: "none",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                >
                  <option value="">Select Scheme</option>
                  {schemes.map((s) => (
                    <option key={s._id} value={s.scheme}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: getTextSecondary(),
                    fontWeight: "500",
                  }}
                >
                  Branch *
                </label>
                <select
                  value={subjectForm.branch}
                  onChange={(e) =>
                    setSubjectForm({ ...subjectForm, branch: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    fontSize: "14px",
                    outline: "none",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                >
                  <option value="">Select Branch</option>
                  {branches.map((b) => (
                    <option key={b._id} value={b.code}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    color: getTextSecondary(),
                    fontWeight: "500",
                  }}
                >
                  Semester *
                </label>
                <select
                  value={subjectForm.semester}
                  onChange={(e) =>
                    setSubjectForm({
                      ...subjectForm,
                      semester: parseInt(e.target.value),
                    })
                  }
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    fontSize: "14px",
                    outline: "none",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                >
                  {semesters.map((s) => (
                    <option key={s} value={s}>
                      Semester {s}
                    </option>
                  ))}
                </select>
              </div>
              {subjectForm.semester <= 2 && (
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      color: getTextSecondary(),
                      fontWeight: "500",
                    }}
                  >
                    Cycle (For Sem 1-2)
                  </label>
                  <select
                    value={subjectForm.cycle}
                    onChange={(e) =>
                      setSubjectForm({ ...subjectForm, cycle: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: `2px solid ${getBorderColor()}`,
                      borderRadius: "12px",
                      fontSize: "14px",
                      outline: "none",
                      background: getInputBackground(),
                      color: getTextColor(),
                    }}
                  >
                    <option value="">Select Cycle</option>
                    <option value="P">Physics Cycle (P)</option>
                    <option value="C">Chemistry Cycle (C)</option>
                  </select>
                </div>
              )}
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={subjectForm.isElective}
                    onChange={(e) =>
                      setSubjectForm({
                        ...subjectForm,
                        isElective: e.target.checked,
                      })
                    }
                    style={{ width: "18px", height: "18px" }}
                  />
                  <span style={{ color: getTextSecondary() }}>
                    This is an Elective Course
                  </span>
                </label>
              </div>
            </div>

            <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
              <button
                onClick={() => setShowSubjectModal(false)}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: isDarkMode ? "rgba(139,92,246,0.1)" : "#f8f9fa",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: getTextColor(),
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSubject}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  boxShadow: "0 4px 14px 0 rgba(139,92,246,0.4)",
                }}
              >
                {editingSubject ? "Update Subject" : "Create Subject"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== BULK IMPORT MODAL ==================== */}
      {showBulkModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
          onClick={() => setShowBulkModal(false)}
        >
          <div
            style={{
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              borderRadius: "28px",
              maxWidth: "900px",
              width: "100%",
              padding: "30px",
              maxHeight: "90vh",
              overflow: "auto",
              border: `1px solid ${getBorderColor()}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
              }}
            >
              <h2
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Bulk Import Subjects
              </h2>
              <button
                onClick={() => setShowBulkModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "20px",
                  cursor: "pointer",
                  color: getTextSecondary(),
                  padding: "8px",
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: getTextSecondary(),
                  fontWeight: "500",
                }}
              >
                Select Format
              </label>
              <div style={{ display: "flex", gap: "20px" }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="radio"
                    value="json"
                    checked={bulkFormat === "json"}
                    onChange={(e) => setBulkFormat(e.target.value)}
                  />
                  <span style={{ color: getTextColor() }}>JSON Format</span>
                </label>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="radio"
                    value="csv"
                    checked={bulkFormat === "csv"}
                    onChange={(e) => setBulkFormat(e.target.value)}
                  />
                  <span style={{ color: getTextColor() }}>CSV Format</span>
                </label>
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: getTextSecondary(),
                  fontWeight: "500",
                }}
              >
                Import Data
              </label>
              {bulkFormat === "json" ? (
                <textarea
                  value={bulkData}
                  onChange={(e) => setBulkData(e.target.value)}
                  rows="12"
                  placeholder='[
  {
    "subjectCode": "BCS301",
    "subjectName": "Data Structures",
    "credits": 4,
    "scheme": "2022",
    "branch": "CS",
    "semester": 3,
    "cycle": null,
    "isElective": false
  }
]'
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    fontSize: "13px",
                    fontFamily: "monospace",
                    outline: "none",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                />
              ) : (
                <textarea
                  value={bulkData}
                  onChange={(e) => setBulkData(e.target.value)}
                  rows="12"
                  placeholder="subjectCode,subjectName,credits,scheme,branch,semester,cycle,isElective
BCS301,Data Structures,4,2022,CS,3,,false
BCS302,Operating Systems,4,2022,CS,3,,false"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "12px",
                    fontSize: "13px",
                    fontFamily: "monospace",
                    outline: "none",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                />
              )}
            </div>

            {bulkData && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={previewBulkData}
                style={{
                  padding: "8px 16px",
                  background: isDarkMode ? "rgba(139,92,246,0.1)" : "#e9ecef",
                  border: `1px solid ${getBorderColor()}`,
                  borderRadius: "10px",
                  cursor: "pointer",
                  marginBottom: "16px",
                  fontSize: "13px",
                  color: getTextColor(),
                }}
              >
                Preview First 5 Items
              </motion.button>
            )}

            {bulkPreview.length > 0 && (
              <div
                style={{
                  marginBottom: "20px",
                  padding: "16px",
                  background: isDarkMode ? "rgba(139,92,246,0.05)" : "#f8f9fa",
                  borderRadius: "12px",
                  overflowX: "auto",
                  border: `1px solid ${getBorderColor()}`,
                }}
              >
                <h4
                  style={{
                    marginBottom: "12px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: getTextColor(),
                  }}
                >
                  Preview:
                </h4>
                <pre
                  style={{
                    fontSize: "12px",
                    overflowX: "auto",
                    color: getTextSecondary(),
                  }}
                >
                  {JSON.stringify(bulkPreview, null, 2)}
                </pre>
              </div>
            )}

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={() => setShowBulkModal(false)}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: isDarkMode ? "rgba(139,92,246,0.1)" : "#f8f9fa",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: getTextColor(),
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkImport}
                disabled={bulkImporting || !bulkData.trim()}
                style={{
                  flex: 1,
                  padding: "12px",
                  background:
                    bulkImporting || !bulkData.trim()
                      ? "#94a3b8"
                      : "linear-gradient(135deg, #10b981, #059669)",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  cursor:
                    bulkImporting || !bulkData.trim()
                      ? "not-allowed"
                      : "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  opacity: bulkImporting || !bulkData.trim() ? 0.6 : 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                {bulkImporting ? (
                  <FaSpinner style={{ animation: "spin 1s linear infinite" }} />
                ) : (
                  <FaFileImport />
                )}
                {bulkImporting ? "Importing..." : "Import Subjects"}
              </button>
            </div>

            <div
              style={{
                marginTop: "20px",
                padding: "16px",
                background: isDarkMode ? "rgba(139,92,246,0.1)" : "#fff5e6",
                borderRadius: "12px",
                fontSize: "12px",
                color: getTextSecondary(),
                border: `1px solid ${getBorderColor()}`,
              }}
            >
              <strong style={{ color: "#8b5cf6" }}>
                📌 Sample JSON Format:
              </strong>
              <pre
                style={{
                  fontSize: "11px",
                  marginTop: "8px",
                  overflowX: "auto",
                  color: getTextSecondary(),
                }}
              >
                {`[
  {
    "subjectCode": "BCS301",
    "subjectName": "Data Structures",
    "credits": 4,
    "scheme": "2022",
    "branch": "CS",
    "semester": 3,
    "cycle": null,
    "isElective": false
  }
]`}
              </pre>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default AdminSGPA;

